import { FilterX, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { EmptyState as BaseEmpty, ErrorState, ListSkeleton, CardSkeleton } from "@/components/ui/states";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export { ErrorState, ListSkeleton, CardSkeleton };

/**
 * Guided empty states.
 * - `first-use` (default): what will appear here + the one next step (`action`).
 * - `filtered`: nothing matches; always offers "Clear filters" (`onClearFilters`). Title/hint default to the kit copy.
 */
export function EmptyState({
  variant = "first-use",
  icon,
  title,
  hint,
  action,
  onClearFilters,
  className,
  compact,
  testId,
}: {
  variant?: "first-use" | "filtered";
  icon?: LucideIcon;
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  onClearFilters?: () => void;
  className?: string;
  compact?: boolean;
  testId?: string;
}) {
  const { t } = useT();
  if (variant === "filtered") {
    return (
      <div className={cn("flex flex-col items-center px-6 text-center", compact ? "py-8" : "py-12", className)} data-testid={testId ?? "empty-filtered"}>
        <div className="mb-3 grid size-11 place-items-center rounded-2xl bg-surface-2 text-ink-3" aria-hidden>
          {(() => {
            const I = icon ?? FilterX;
            return <I className="size-5" />;
          })()}
        </div>
        <p className="text-[14.5px] font-semibold text-ink">{title ?? t("kit.noMatches")}</p>
        <p className="mt-1 max-w-sm text-[13px] text-ink-3">{hint ?? t("kit.noMatchesHint")}</p>
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {onClearFilters && (
            <Button size="sm" variant="outline" onClick={onClearFilters} data-testid="empty-clear-filters">
              {t("kit.clearFilters")}
            </Button>
          )}
          {action}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={testId ?? "empty-first-use"}>
      <BaseEmpty icon={icon ?? FilterX} title={title ?? t("common.states.empty")} hint={hint} action={action} className={cn(compact && "py-8", className)} />
    </div>
  );
}

/** Table placeholder while rows load: a header line and `rows` rows of `cols` cells. */
export function TableSkeleton({ rows = 6, cols = 4, className }: { rows?: number; cols?: number; className?: string }) {
  const { t } = useT();
  return (
    <div role="status" aria-busy="true" aria-label={t("kit.loading")} className={cn("overflow-hidden rounded-2xl border border-line bg-surface", className)}>
      <div className="flex gap-4 border-b border-line px-4 py-3">
        {Array.from({ length: cols }, (_, c) => (
          <Skeleton key={c} className="h-3 flex-1" style={{ maxWidth: c === 0 ? "30%" : "16%" }} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-line px-4 py-[var(--row-pad-y)] last:border-0">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className="h-3 flex-1" style={{ maxWidth: c === 0 ? `${26 + ((r * 13) % 18)}%` : "14%" }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Page placeholder: header block + a few cards. Use while the first query of a page loads. */
export function PageSkeleton({ cards = 3, className }: { cards?: number; className?: string }) {
  const { t } = useT();
  return (
    <div role="status" aria-busy="true" aria-label={t("kit.loading")} className={className}>
      <Skeleton className="mb-2 h-6 w-48" />
      <Skeleton className="mb-6 h-3.5 w-80 max-w-full" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: cards }, (_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

type QueryLike<T> = { data: T | undefined; isPending?: boolean; isLoading?: boolean; isError: boolean; error: unknown; refetch: () => unknown };

/**
 * One switch for the three states of a query: loading → `loading` (default ListSkeleton), error → ErrorState with
 * retry, empty (when `isEmpty(data)`) → `empty`, otherwise `children(data)`.
 *
 *   <QueryStates query={q} isEmpty={(d) => !d.items.length} empty={<EmptyState title=… action=… />}>
 *     {(d) => <List items={d.items} />}
 *   </QueryStates>
 */
export function QueryStates<T>({ query, loading, empty, isEmpty, children }: { query: QueryLike<T>; loading?: ReactNode; empty?: ReactNode; isEmpty?: (d: T) => boolean; children: (d: T) => ReactNode }) {
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return <>{loading ?? <ListSkeleton />}</>;
  if (empty && isEmpty?.(query.data)) return <>{empty}</>;
  return <>{children(query.data)}</>;
}
